<script setup>
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/vue";
import { EllipsisVerticalIcon } from "@heroicons/vue/24/solid";
import {
  formatNumber,
  formatPercentage,
  formatPrice,
} from "@js/utils/formatter";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { computed, ref } from "vue";
import { Line } from "vue-chartjs";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const {
  labels,
  datasets,
  title,
  typeAxis1 = "number",
  withDecimalsAxis1 = true,
  enableAxis2 = false,
  typeAxis2 = "number",
  withDecimalsAxis2 = true,
  reverseAxis1 = false,
  reverseAxis2 = false,
  options,
} = defineProps({
  labels: {
    type: Array,
    required: true,
  },
  datasets: {
    type: Array,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  typeAxis1: {
    type: String,
    required: false,
  },
  withDecimalsAxis1: {
    type: Boolean,
    required: false,
  },
  enableAxis2: {
    type: Boolean,
    required: false,
  },
  typeAxis2: {
    type: String,
    required: false,
  },
  withDecimalsAxis2: {
    type: Boolean,
    required: false,
    default: true,
  },
  reverseAxis1: {
    type: Boolean,
    required: false,
    default: false,
  },
  reverseAxis2: {
    type: Boolean,
    required: false,
    default: false,
  },
  options: {
    type: Object,
    required: false,
  },
});

const chartData = computed(() => {
  return {
    labels,
    datasets,
  };
});

const chartOptions = ref({
  interaction: {
    intersect: false,
    mode: "index",
  },
  scales: {
    ...(enableAxis2
      ? {
          y: {
            position: "left",
            reverse: reverseAxis1,
            ticks: {
              callback: (value) => {
                if (typeAxis1 === "percentage") {
                  return formatPercentage(value, false, withDecimalsAxis1);
                }

                if (typeAxis1 === "price") {
                  return formatPrice(value, withDecimalsAxis1);
                }

                return (
                  formatNumber(value, withDecimalsAxis1)
                );
              },
            },
          },
          y1: {
            position: "right",
            reverse: reverseAxis2,
            ticks: {
              callback: (value) => {
                if (typeAxis2 === "percentage") {
                  return formatPercentage(value, false, withDecimalsAxis2);
                }

                if (typeAxis2 === "price") {
                  return formatPrice(value, withDecimalsAxis2);
                }

                return (
                  formatNumber(value, withDecimalsAxis2)
                );
              },
            },
          },
        }
      : {
          y: {
            reverse: reverseAxis1,
            ticks: {
              callback: (value) => {
                if (typeAxis1 === "percentage") {
                  return formatPercentage(value, false, withDecimalsAxis1);
                }

                if (typeAxis1 === "price") {
                  return formatPrice(value, withDecimalsAxis1);
                }

                return (
                  formatNumber(value, withDecimalsAxis1)
                );
              },
            },
          },
        }),
  },
  plugins: {
    tooltip: {
      callbacks: {
        label: (ctx) => {
          let index = ctx.dataIndex;
          let label = ctx.dataset.labels[index];
          let value = ctx.dataset.data[index];
          let yAxisId = ctx.dataset.yAxisID;

          let type = typeAxis1;

          if (yAxisId === "y1") {
            type = typeAxis2;
          }

          let withDecimals = withDecimalsAxis1;

          if (yAxisId === "y1") {
            withDecimals = withDecimalsAxis2;
          }

          if (type === "percentage") {
            value = formatPercentage(value, false, withDecimals);
          } else if (type === "price") {
            value = formatPrice(value, withDecimals);
          } else {
            value = formatNumber(value, withDecimals);
          }

          return label + " - " + value;
        },
      },
      enabled: false,
      position: "nearest",
      external: externalTooltipHandler,
    },
  },
});

const getOrCreateTooltip = (chart) => {
  let tooltipEl = chart.canvas.parentNode.querySelector("div");

  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.style.background = "rgba(0, 0, 0, 0.7)";
    tooltipEl.style.borderRadius = "3px";
    tooltipEl.style.color = "white";
    tooltipEl.style.opacity = 1;
    tooltipEl.style.pointerEvents = "none";
    tooltipEl.style.position = "absolute";
    tooltipEl.style.transform = "translate(-50%, 0)";
    tooltipEl.style.transition = "all .1s ease";
    tooltipEl.style.zIndex = 1000;

    const table = document.createElement("table");
    table.style.margin = "0px";

    tooltipEl.appendChild(table);
    chart.canvas.parentNode.appendChild(tooltipEl);
  }

  return tooltipEl;
};

function externalTooltipHandler(context) {
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltip(chart);

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  if (tooltip.body) {
    const bodyLines = tooltip.body.map((b) => b.lines);

    const tableHead = document.createElement("thead");

    const tableBody = document.createElement("tbody");

    let previousGroup = null;

    bodyLines.forEach((body, i) => {
      if (tooltip.dataPoints[i].dataset.group !== previousGroup) {
        previousGroup = tooltip.dataPoints[i].dataset.group;

        let tr = document.createElement("tr");
        tr.style.backgroundColor = "inherit";
        tr.style.borderWidth = 0;

        let td = document.createElement("td");
        td.style.borderWidth = 0;
        td.style.whiteSpace = "nowrap";
        td.style.fontWeight = "bold";
        td.style.fontSize = "15px";

        let text = document.createTextNode(previousGroup);

        td.appendChild(text);
        tr.appendChild(td);
        tableBody.appendChild(tr);
      }

      let colors = tooltip.labelColors[i];

      let span = document.createElement("span");
      span.style.background = colors.backgroundColor;
      span.style.borderColor = colors.borderColor;
      span.style.borderWidth = "2px";
      span.style.marginRight = "10px";
      span.style.height = "10px";
      span.style.width = "10px";
      span.style.display = "inline-block";

      let tr = document.createElement("tr");
      tr.style.backgroundColor = "inherit";
      tr.style.borderWidth = 0;

      let td = document.createElement("td");
      td.style.borderWidth = 0;
      td.style.whiteSpace = "nowrap";
      td.style.fontSize = "13px";

      let text = document.createTextNode(body);

      td.appendChild(span);
      td.appendChild(text);
      tr.appendChild(td);
      tableBody.appendChild(tr);
    });

    const tableRoot = tooltipEl.querySelector("table");

    while (tableRoot.firstChild) {
      tableRoot.firstChild.remove();
    }

    tableRoot.appendChild(tableHead);
    tableRoot.appendChild(tableBody);
  }

  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

  tooltipEl.style.opacity = 1;
  tooltipEl.style.left = positionX + tooltip.caretX + "px";
  tooltipEl.style.top = positionY + tooltip.caretY + "px";
  tooltipEl.style.font = tooltip.options.bodyFont.string;
  tooltipEl.style.padding =
    tooltip.options.padding + "px " + tooltip.options.padding + "px";
}
</script>

<template>
  <div class="rounded border border-gray-200 bg-white">
    <div
      class="flex h-14 w-full items-center justify-between gap-4 border-b border-gray-200 px-4 py-3"
    >
      <div class="text-gray-500">
        {{ title }}
      </div>

      <Menu v-if="options" as="div" class="relative max-h-8">
        <MenuButton>
          <EllipsisVerticalIcon
            class="size-8 text-gray-300 duration-200 hover:text-gray-500"
          />
        </MenuButton>

        <MenuItems
          class="absolute right-0 top-10 z-10 flex flex-col items-start gap-px overflow-hidden rounded bg-gray-100"
        >
          <MenuItem v-for="(option, index) in options" :key="index">
            <button
              type="button"
              class="w-full whitespace-nowrap px-4 py-2 text-sm text-gray-700 duration-200 hover:bg-gray-100"
              :class="{
                'bg-white': !option.active,
                'bg-gray-100': option.active,
              }"
              @click="option.action"
            >
              {{ option.label }}
            </button>
          </MenuItem>
        </MenuItems>
      </Menu>
    </div>

    <div class="px-4 py-3">
      <Line :data="chartData" :options="chartOptions" />
    </div>
  </div>
</template>

<style scoped>
:deep(canvas) {
  max-height: 400px;
}
</style>
